import React, { useState, useMemo } from "react";
import { Flex, Spin } from "antd";
import {
  MessageOutlined,
  PhoneOutlined,
  FileTextOutlined,
  AuditOutlined,
  MailOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

function CompanyTimeline({
  lead,
  company,
  remarks = [],
  invoices = [],
  quotations = [],
  proposals = [],
  emails = [],
  historyLogs = [],
  loading = false,
  hideHeaderTitle = false,
}) {
  const [filterType, setFilterType] = useState("all");

  const timelineItems = useMemo(() => {
    const items = [];
    const seenRemarks = new Set();

    // 1. History Logs (Real backend activities & remark events with authentic timestamps)
    (historyLogs || []).forEach((log, idx) => {
      const rawDate = log.timestamp || log.createdAt || log.updatedAt || log.date;
      const parsedDate = rawDate ? dayjs(rawDate) : null;
      const validDate = parsedDate && parsedDate.isValid() ? parsedDate : null;
      if (!validDate) return;

      const actionStr = String(log.action || "");
      const isRemark = actionStr.toLowerCase().includes("remark");
      const actor = log.changedByName || log.changedBy;
      const actorStr = actor && actor !== "System" ? ` • By: ${actor}` : "";

      if (isRemark) {
        const text = log.newValue || log.details || log.remark || log.comment || "";
        if (text) {
          seenRemarks.add(text.trim().toLowerCase());
          const detailsStr = log.details && log.details !== text ? ` • ${log.details}` : "";
          items.push({
            id: `hist-rem-${log._id || idx}`,
            type: "remark",
            date: validDate,
            title: `Remark: ${text.length > 40 ? text.slice(0, 40) + "..." : text}`,
            description: `${text}${detailsStr}${actorStr}`,
          });
        }
      } else {
        const detailsStr = log.details ? `${log.details} • ` : "";
        const contactStr = log.contactName ? `Contact: ${log.contactName}` : "";
        const combined = `${detailsStr}${contactStr}${actorStr}`.replace(/^\s*•\s*|\s*•\s*$/g, "");
        items.push({
          id: `hist-act-${log._id || idx}`,
          type: "activity",
          date: validDate,
          title: log.action ? `${log.action}${log.newValue ? `: ${log.newValue}` : ""}` : "Activity",
          description: combined || (log.action || "Activity"),
        });
      }
    });

    // 2. Remarks (fallback authentic date from lead/company, avoiding current time)
    (remarks || []).forEach((rem, idx) => {
      const text = typeof rem === "string" ? rem : rem?.text || rem?.remark || rem?.comment || rem?.note || rem?.newValue || rem?.details || "";
      if (!text || seenRemarks.has(text.trim().toLowerCase())) return;

      const rawDate = typeof rem === "object" ? rem?.date || rem?.createdAt || rem?.timestamp || rem?.updatedAt : null;
      let validDate;
      if (rawDate) {
        const parsed = dayjs(rawDate);
        validDate = parsed.isValid() ? parsed : null;
      }
      if (!validDate) {
        const fallback = lead?.updatedAt || lead?.createdAt || company?.updatedAt || company?.createdAt;
        validDate = fallback ? dayjs(fallback) : dayjs(lead?.createdAt || undefined);
      }

      items.push({
        id: `rem-${idx}-${text}`,
        type: "remark",
        date: validDate,
        title: `Remark: ${text.length > 40 ? text.slice(0, 40) + "..." : text}`,
        description: text,
      });
    });

    // Invoices
    (invoices || []).forEach((inv, idx) => {
      const rawDate = inv.date || inv.invoiceDate || inv.createdAt;
      const parsedDate = rawDate ? dayjs(rawDate) : dayjs();
      const validDate = parsedDate.isValid() ? parsedDate : dayjs();
      items.push({
        id: `inv-${inv.id || inv._id || idx}`,
        type: "invoice",
        date: validDate,
        title: `Invoice #${inv.invoiceNumber || inv.number || "INV-001"}`,
        description: `Total: ${inv.currency || "₹"}${inv.total || inv.grandTotal || 0} • Status: ${inv.status || "Paid"}`,
      });
    });

    // Quotations
    (quotations || []).forEach((q, idx) => {
      const rawDate = q.date || q.quotationDate || q.createdAt;
      const parsedDate = rawDate ? dayjs(rawDate) : dayjs();
      const validDate = parsedDate.isValid() ? parsedDate : dayjs();
      items.push({
        id: `quot-${q.id || q._id || idx}`,
        type: "quotation",
        date: validDate,
        title: `Quotation #${q.quotationNumber || q.number || "QUOT-001"}`,
        description: `Amount: ${q.currency || "₹"}${q.total || q.grandTotal || 0} • Status: ${q.status || "Draft"}`,
      });
    });

    // Proposals
    (proposals || []).forEach((prop, idx) => {
      const rawDate = prop.date || prop.createdAt;
      const parsedDate = rawDate ? dayjs(rawDate) : dayjs();
      const validDate = parsedDate.isValid() ? parsedDate : dayjs();
      items.push({
        id: `prop-${prop.id || prop._id || idx}`,
        type: "proposal",
        date: validDate,
        title: `Proposal Generated`,
        description: prop.title
          ? `Proposal "${prop.title}" was generated.`
          : `A proposal PDF was generated and downloaded.`,
      });
    });

    // Emails
    (emails || []).forEach((em, idx) => {
      const rawDate = em.date || em.createdAt;
      const parsedDate = rawDate ? dayjs(rawDate) : dayjs();
      const validDate = parsedDate.isValid() ? parsedDate : dayjs();
      items.push({
        id: `email-${em.id || em._id || idx}`,
        type: "email",
        date: validDate,
        title: `Email Sent: ${em.subject || "Follow-up"}`,
        description: `To: ${em.to || lead?.companyEmail || "Client"}`,
      });
    });

    // Real Call Logs & Activities
    const rawActivities = [
      ...(Array.isArray(lead?.callLogs) ? lead.callLogs : []),
      ...(Array.isArray(lead?.activities) ? lead.activities : []),
      ...(Array.isArray(company?.callLogs) ? company.callLogs : []),
      ...(Array.isArray(company?.activities) ? company.activities : []),
    ];

    rawActivities.forEach((act, idx) => {
      const rawDate = act.date || act.callDate || act.createdAt || act.timestamp;
      const parsedDate = rawDate ? dayjs(rawDate) : dayjs();
      const validDate = parsedDate.isValid() ? parsedDate : dayjs();
      const isCall = act.duration !== undefined || act.callType || act.type === "call";
      const callerName = act.employeeName || act.callerName || act.employee || "";
      const contactInfo = act.contactName || lead?.contactName || "Contact";
      const phoneInfo = act.contactNumber || act.phone || lead?.contactNumber || "";
      const durationStr = act.duration !== undefined ? ` • Duration: ${act.duration}s` : "";
      const empStr = callerName ? `Employee: ${callerName} • ` : "";

      items.push({
        id: `act-${act.id || act._id || idx}`,
        type: "activity",
        date: validDate,
        title: act.title || (isCall ? `Phone Call (${act.callType || "Outgoing"})` : "Activity"),
        description: act.description || `${empStr}Contact: ${contactInfo} (${phoneInfo})${durationStr}`,
      });
    });

    if (rawActivities.length === 0 && lead?.callSummary?.lastCallDate) {
      items.push({
        id: `call-summary-${lead._id || lead.leadId || "1"}`,
        type: "activity",
        date: dayjs(lead.callSummary.lastCallDate),
        title: "Phone Call (Connected)",
        description: `Contact: ${lead.contactName || "Representative"} (${lead.contactNumber || ""}) • Connected Calls: ${lead.callSummary.connected || 1}`,
      });
    }

    // Sort by date descending
    return items.sort((a, b) => b.date.valueOf() - a.date.valueOf());
  }, [remarks, invoices, quotations, proposals, emails, historyLogs, lead, company]);

  const filteredItems = useMemo(() => {
    if (filterType === "all") return timelineItems;
    return timelineItems.filter((i) => i.type === filterType);
  }, [timelineItems, filterType]);

  const getIcon = (type) => {
    switch (type) {
      case "remark":
        return <MessageOutlined style={{ fontSize: 14 }} />;
      case "invoice":
      case "proposal":
        return <FileTextOutlined style={{ fontSize: 14 }} />;
      case "quotation":
        return <AuditOutlined style={{ fontSize: 14 }} />;
      case "email":
        return <MailOutlined style={{ fontSize: 14 }} />;
      case "activity":
      default:
        return <PhoneOutlined style={{ fontSize: 14 }} />;
    }
  };

  const filterButtons = [
    { key: "all", label: "All" },
    { key: "activity", label: "Activities" },
    { key: "remark", label: "Remarks" },
    { key: "invoice", label: "Invoices" },
    { key: "quotation", label: "Quotations" },
    { key: "proposal", label: "Proposals" },
    { key: "email", label: "Emails" },
  ];

  return (
    <div>
      {/* Header with Title and Filter Pills */}
      <Flex justify={hideHeaderTitle ? "flex-start" : "space-between"} align="center" style={{ marginBottom: 18 }} wrap="wrap" gap={12}>
        {!hideHeaderTitle && (
          <span style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>
            Overall History
          </span>
        )}

        <Flex gap={8} wrap="wrap">
          {filterButtons.map((btn) => {
            const isActive = filterType === btn.key;
            return (
              <button
                key={btn.key}
                type="button"
                onClick={() => setFilterType(btn.key)}
                style={{
                  padding: "4px 12px",
                  borderRadius: 16,
                  fontSize: 12,
                  fontWeight: 500,
                  border: "1px solid",
                  borderColor: isActive ? "#3b82f6" : "#e2e8f0",
                  background: isActive ? "#3b82f6" : "#f8fafc",
                  color: isActive ? "#ffffff" : "#475569",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                {btn.label}
              </button>
            );
          })}
        </Flex>
      </Flex>

      {/* Timeline List */}
      {loading && filteredItems.length === 0 ? (
        <div style={{ padding: "40px 0", textAlign: "center" }}>
          <Spin size="middle" tip="Loading history..." />
        </div>
      ) : filteredItems.length === 0 ? (
        <div style={{ padding: "40px 0", textAlign: "center", color: "#94a3b8" }}>
          No history available for the selected filter.
        </div>
      ) : (
        <Spin spinning={loading} tip="Updating history...">
          <div style={{ position: "relative", paddingLeft: 32 }}>
            {/* Vertical Connecting Line */}
            <div
              style={{
                position: "absolute",
                top: 12,
                bottom: 12,
                left: 10,
                width: 2,
                background: "#e2e8f0",
              }}
            />

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {filteredItems.map((item) => (
                <div key={item.id} style={{ position: "relative" }}>
                  {/* Timeline Node Icon */}
                  <div
                    style={{
                      position: "absolute",
                      left: -32,
                      top: 14,
                      width: 22,
                      height: 22,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#94a3b8",
                      background: "#ffffff",
                      zIndex: 1,
                    }}
                  >
                    {getIcon(item.type)}
                  </div>

                  {/* Timeline Card */}
                  <div
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: 12,
                      padding: "14px 18px",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                    }}
                  >
                    <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6, fontWeight: 500 }}>
                      {item.date.format("MMM DD, YYYY, h:mm:ss A")}
                    </div>
                    <div
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#0f172a",
                        lineHeight: 1.3,
                      }}
                    >
                      {item.title}
                    </div>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 13,
                        color: "#475569",
                        lineHeight: 1.5,
                      }}
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Spin>
      )}
    </div>
  );
}

export default CompanyTimeline;
